<script setup lang="ts">
import {ref, nextTick, onMounted, onUnmounted} from 'vue';
import {DELETE, GET, PATCH, POST} from '../modules/fetch.ts';
import {renderPreviewPanelContent} from '../features/repo-editor.ts';

type ChatRole = 'user' | 'assistant';

type ChatMessage = {
  id: number;
  turnId: number;
  role: ChatRole;
  status: string;
  content: string;
  // Transient tool activity, live from SSE, not persisted: the tool DRCY is
  // currently running, and how many tools ran this turn (shown as a summary
  // once the turn finishes).
  activity: string;
  toolCount: number;
};

// Wire shape from the Hub (api.DrcyChatMessageEntry) and SSE event payloads.
type MessageEntry = {
  id?: number;
  turn_id?: number;
  role: ChatRole;
  status?: string;
  content?: string;
};

type ChatArtifact = {
  id: number;
  turn_id: number;
  title: string;
  filename: string;
  format: 'markdown' | 'csv';
  media_type: string;
  content: string;
  revision: number;
  // 'committing' is a transient backend state during a commit; the list endpoint
  // filters it out, but include it so the type matches every value the API can send.
  state: 'draft' | 'committing' | 'committed';
  source_ref?: string;
  source_commit_sha?: string;
};

const props = withDefaults(
  defineProps<{
    // Chat scope: "pr:<index>" (a design review) or "repo:<branch>".
    scope: string;
    repoLink: string;
    defaultBranch: string;
    // Branch a committed artifact targets by default: the design review's head
    // branch for a pr: scope, or the branch for a repo: scope. Falls back to
    // defaultBranch when empty.
    commitBranch?: string;
    drcyAvatar: string;
    thinkingSrc?: string;
    userName?: string;
    userAvatar?: string;
    userHomeLink?: string;
  }>(),
  {thinkingSrc: '', userName: 'You', userAvatar: '', userHomeLink: '', commitBranch: ''},
);

const messages = ref<ChatMessage[]>([]);
const artifacts = ref<ChatArtifact[]>([]);
const activeArtifactId = ref<number | null>(null);
const artifactSidebarOpen = ref(false);
const editingArtifactId = ref<number | null>(null);
const editTitle = ref('');
const editFilename = ref('');
const editContent = ref('');
// Captured when editing begins so Save uses the revision the user started from,
// not a newer one an SSE refresh may have swapped into the artifact object;
// that keeps optimistic locking honest (a concurrent edit yields a 409).
const editRevision = ref<number>(0);
const artifactError = ref('');
// A success notice naming the branch a commit landed on, with a link to it. The
// commit can fall back to an auto-named branch (e.g. when the target is
// protected), so report where it actually went rather than navigating away.
const artifactNotice = ref('');
const artifactNoticeUrl = ref('');
const committing = ref(false);
const scroller = ref<HTMLElement>();

let eventSource: EventSource | null = null;

// Fallback poll while a turn is in flight. SSE is the primary channel, but a
// silently-dead-but-"open" stream (server restart, proxy timeout, sleep/wake)
// never fires 'open'/'error', so the reconnect catch-up can't run and the turn
// would spin forever. Polling history reconciles content and terminal status
// regardless of the stream's health, then stops once nothing is in flight.
let pollTimer: number | null = null;
const POLL_INTERVAL_MS = 3000;

// Last emitted busy state, so we only notify the composer on transitions.
let lastBusy = false;

// Message bodies we render markdown into, keyed by message id, plus per-message
// debounce timers so streaming re-renders don't hammer the server.
const bubbleEls = new Map<number, HTMLElement>();
const renderTimers = new Map<number, number>();
const RENDER_DEBOUNCE_MS = 350;
// Content most recently rendered (or dispatched to /markup) per message id, so
// we skip re-rendering unchanged content (e.g. every 3s poll re-upserts every
// message) and can discard a stale /markup response.
const renderedContent = new Map<number, string>();

const baseUrl = `${props.repoLink}/drcy_chat`;
const scopeQuery = `scope=${encodeURIComponent(props.scope)}`;

function authorName(message: ChatMessage): string {
  return message.role === 'assistant' ? 'DRCY' : props.userName;
}
function avatarSrc(message: ChatMessage): string {
  return message.role === 'assistant' ? props.drcyAvatar : props.userAvatar;
}
function avatarHref(message: ChatMessage): string | undefined {
  return message.role === 'assistant' ? undefined : props.userHomeLink || undefined;
}

function upsert(entry: MessageEntry) {
  if (!entry.id) return;
  const existing = messages.value.find((m) => m.id === entry.id);
  const next: ChatMessage = {
    id: entry.id,
    turnId: entry.turn_id ?? 0,
    role: entry.role,
    status: entry.status ?? '',
    content: entry.content ?? '',
    activity: existing?.activity ?? '',
    toolCount: existing?.toolCount ?? 0,
  };
  const index = messages.value.findIndex((m) => m.id === next.id);
  if (index >= 0) {
    const prev = messages.value[index];
    // While a turn streams, the SSE 'chunk' handler grows the assistant content
    // in place. A concurrent history poll can carry an older, shorter snapshot;
    // merging it would truncate the live text and the next chunk would append
    // onto that stale prefix (garbled output). So for an in-flight assistant,
    // never let a merge shrink the content — SSE owns it until the turn ends,
    // at which point the terminal snapshot is at least as long and applies.
    const inFlightAssistant =
      prev.role === 'assistant' && (prev.status === 'pending' || prev.status === 'running');
    if (inFlightAssistant && next.content.length < prev.content.length) {
      next.content = prev.content;
    }
    messages.value[index] = {...prev, ...next};
  } else {
    messages.value.push(next);
    messages.value.sort((a, b) => a.id - b.id);
  }
  if (next.content) renderNow(messages.value.find((m) => m.id === next.id));
  scrollToBottom();
  syncPolling();
}

function assistantForTurn(turnId: number): ChatMessage | undefined {
  return messages.value.find((m) => m.turnId === turnId && m.role === 'assistant');
}

function setBubble(id: number, el: Element | null) {
  if (el) bubbleEls.set(id, el as HTMLElement);
  else bubbleEls.delete(id);
}

// Render a message's markdown through the Hub's own markup pipeline (server
// render + the global `.markup` enhancers: KaTeX, code highlight, mermaid) —
// the same path comment previews use, so chat content looks like comments.
async function renderMarkdown(message: ChatMessage | undefined) {
  if (!message || !message.content) return;
  // Snapshot the content we are about to render; if it changes while we await
  // /markup, a newer render will run and this (stale) response is discarded so
  // it can't overwrite the bubble with older partial content.
  const content = message.content;
  renderedContent.set(message.id, content);
  await nextTick();
  const el = bubbleEls.get(message.id);
  if (!el) return;
  try {
    const formData = new FormData();
    formData.append('mode', 'comment');
    formData.append('context', props.repoLink);
    formData.append('text', content);
    const res = await POST(`${props.repoLink}/markup`, {data: formData});
    if (message.content !== content) return; // superseded while awaiting
    if (!res.ok) {
      el.textContent = content;
      return;
    }
    const html = await res.text();
    if (message.content !== content) return; // superseded while awaiting
    renderPreviewPanelContent(el, html);
  } catch (err) {
    console.error('Failed to render DRCY markdown', err);
    if (message.content === content) el.textContent = content;
  }
  scrollToBottom();
}

function scheduleRender(message: ChatMessage | undefined) {
  // Skip when the current content is already what we last rendered/dispatched,
  // so repeated upserts of unchanged content (every poll tick) don't re-render.
  if (!message || renderedContent.get(message.id) === message.content) return;
  const existing = renderTimers.get(message.id);
  if (existing) clearTimeout(existing);
  renderTimers.set(
    message.id,
    window.setTimeout(() => {
      renderTimers.delete(message.id);
      renderMarkdown(message);
    }, RENDER_DEBOUNCE_MS),
  );
}

function renderNow(message: ChatMessage | undefined) {
  if (!message || renderedContent.get(message.id) === message.content) return;
  const existing = renderTimers.get(message.id);
  if (existing) {
    clearTimeout(existing);
    renderTimers.delete(message.id);
  }
  renderMarkdown(message);
}

// How close to the bottom (px) still counts as "following" the conversation.
const SCROLL_FOLLOW_THRESHOLD_PX = 80;

function isNearBottom(el: HTMLElement): boolean {
  return el.scrollHeight - el.scrollTop - el.clientHeight <= SCROLL_FOLLOW_THRESHOLD_PX;
}

function scrollToBottom() {
  // Respect a user who scrolled up to read earlier messages: only auto-follow
  // new content when they're already near the bottom. Checked synchronously
  // (before nextTick) so it reflects the position before this update rendered.
  const el = scroller.value;
  if (el && !isNearBottom(el)) return;
  nextTick(() => {
    const target = scroller.value;
    if (target) target.scrollTop = target.scrollHeight;
  });
}

function clearAll() {
  for (const timer of renderTimers.values()) clearTimeout(timer);
  renderTimers.clear();
  renderedContent.clear();
  bubbleEls.clear();
  messages.value = [];
  artifacts.value = [];
  activeArtifactId.value = null;
  artifactSidebarOpen.value = false;
  editingArtifactId.value = null;
  artifactError.value = '';
  artifactNotice.value = '';
  artifactNoticeUrl.value = '';
  syncArtifactDirtyState();
  syncPolling();
}

function syncArtifactDirtyState() {
  document.dispatchEvent(new CustomEvent('drcy-chat-artifacts-dirty', {detail: artifacts.value.length > 0}));
}

async function loadArtifacts(reveal = false) {
  try {
    const res = await GET(`${baseUrl}/artifacts?${scopeQuery}`);
    if (!res.ok) return;
    const data = await res.json();
    artifacts.value = data ?? [];
    const validIds = new Set(artifacts.value.map((artifact) => artifact.id));
    if (activeArtifactId.value !== null && !validIds.has(activeArtifactId.value)) {
      activeArtifactId.value = artifacts.value[0]?.id ?? null;
    }
    if (artifacts.value.length === 0) {
      editingArtifactId.value = null;
      // Keep the sidebar open while a success notice is pending, so it survives
      // the committed draft leaving the list.
      if (!artifactNotice.value) artifactSidebarOpen.value = false;
    } else if (reveal) {
      artifactSidebarOpen.value = true;
      activeArtifactId.value ??= artifacts.value[0]?.id ?? null;
    }
    syncArtifactDirtyState();
  } catch (err) {
    console.error('Failed to load DRCY artifacts', err);
  }
}

function toggleArtifactDetails(artifact: ChatArtifact) {
  if (activeArtifactId.value === artifact.id) {
    activeArtifactId.value = null;
    editingArtifactId.value = null;
    artifactError.value = '';
    return;
  }
  activeArtifactId.value = artifact.id;
  artifactSidebarOpen.value = true;
  editingArtifactId.value = null;
  artifactError.value = '';
}

function closeArtifactSidebar() {
  artifactSidebarOpen.value = false;
  editingArtifactId.value = null;
  artifactError.value = '';
  artifactNotice.value = '';
  artifactNoticeUrl.value = '';
}

function handleArtifactKeydown(event: KeyboardEvent) {
  if (event.key !== 'Escape' || !artifactSidebarOpen.value) return;
  closeArtifactSidebar();
}

function artifactDownloadUrl(artifact: ChatArtifact): string {
  return `${baseUrl}/artifacts/${artifact.id}/download?${scopeQuery}`;
}

function beginEdit(artifact: ChatArtifact) {
  editingArtifactId.value = artifact.id;
  editTitle.value = artifact.title;
  editFilename.value = artifact.filename;
  editContent.value = artifact.content;
  editRevision.value = artifact.revision;
  artifactError.value = '';
  artifactNotice.value = '';
  artifactNoticeUrl.value = '';
}

async function saveArtifact(artifact: ChatArtifact) {
  artifactError.value = '';
  const res = await PATCH(`${baseUrl}/artifacts/${artifact.id}?${scopeQuery}`, {
    data: {
      revision: editRevision.value,
      title: editTitle.value,
      filename: editFilename.value,
      content: editContent.value,
    },
  });
  if (!res.ok) {
    if (res.status === 409) {
      // The draft moved on. Exit edit mode so the stale editRevision can't drive
      // an endless 409 loop; reopening the reloaded draft re-captures its current
      // revision and content.
      artifactError.value = 'This artifact changed elsewhere. Reloaded the latest draft; reopen it to apply your edit.';
      editingArtifactId.value = null;
    } else {
      artifactError.value = 'Could not save this artifact.';
    }
    await loadArtifacts();
    return;
  }
  editingArtifactId.value = null;
  await loadArtifacts(true);
}

async function deleteArtifact(artifact: ChatArtifact) {
  if (!window.confirm(`Delete "${artifact.title}"? This cannot be undone.`)) return;
  artifactError.value = '';
  const res = await DELETE(`${baseUrl}/artifacts/${artifact.id}?${scopeQuery}`, {
    data: {revision: artifact.revision},
  });
  if (!res.ok) {
    artifactError.value = res.status === 409 ?
      'This artifact changed elsewhere. Reloaded the latest draft.' :
      'Could not delete this artifact.';
  }
  if (activeArtifactId.value === artifact.id) activeArtifactId.value = null;
  await loadArtifacts();
}

function artifactCommitMessage(artifact: ChatArtifact): string {
  return `docs: add DRCY artifact ${artifact.filename}`;
}

function artifactBranch(artifact: ChatArtifact): string {
  const stem = artifact.filename
    .replace(/\.[^.]+$/, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  return `drcy/artifacts/${stem || 'artifact'}-${artifact.id}`;
}

function commitTargetBranch(): string {
  if (props.commitBranch) return props.commitBranch;
  return props.scope.startsWith('repo:') ? props.scope.slice('repo:'.length) : props.defaultBranch;
}

async function commitArtifact(artifact: ChatArtifact) {
  if (committing.value) return;
  const branch = commitTargetBranch();
  if (!window.confirm(`Save "${artifact.filename}" to the "${branch}" branch of this repository?`)) return;
  committing.value = true;
  artifactError.value = '';
  artifactNotice.value = '';
  artifactNoticeUrl.value = '';
  try {
    const commit = (newBranch = '') => POST(`${baseUrl}/artifacts/commit?${scopeQuery}`, {
      data: {artifact_ids: [artifact.id], branch, new_branch: newBranch, message: artifactCommitMessage(artifact)},
    });
    // A protected target branch returns 403; retry once onto an auto-named
    // branch so the save still lands somewhere the user can open a PR from.
    let res = await commit();
    if (res.status === 403) res = await commit(artifactBranch(artifact));
    if (!res.ok) {
      artifactError.value = res.status === 403 ?
        'You need code write access to commit this artifact.' :
        'Could not commit this artifact.';
      return;
    }
    const data = await res.json();
    // Report the branch it actually landed on (which may be the fallback, not the
    // one named in the confirm) with a link, rather than navigating away. Set the
    // notice BEFORE reloading: committing the last draft empties the list, and
    // loadArtifacts must see a pending notice so it keeps the sidebar open to
    // render it.
    artifactNotice.value = `Saved to the "${data.branch}" branch.`;
    artifactNoticeUrl.value = data.commit_url ?? '';
    await loadArtifacts();
  } finally {
    committing.value = false;
  }
}

function warnBeforeUnload(event: BeforeUnloadEvent) {
  if (artifacts.value.length === 0) return;
  event.preventDefault();
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  event.returnValue = '';
}

// Export the conversation as a Markdown file. Client-side only — the messages
// are already in memory — so no design data leaves the browser beyond what the
// user chooses to save.
function exportMarkdown() {
  const lines: string[] = [
    `# Chat with DRCY: ${props.repoLink} (${props.scope})`,
    '',
  ];
  for (const message of messages.value) {
    const body = message.content.trim();
    if (!body) continue;
    lines.push(`**${authorName(message)}**`, '', body, '', '---', '');
  }
  const blob = new Blob([lines.join('\n')], {type: 'text/markdown'});
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `drcy-chat-${props.scope.replace(/[^a-z0-9]+/gi, '-')}.md`;
  document.body.append(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

// Open the browser print dialog. A scoped print stylesheet isolates the chat so
// "Save as PDF" produces a clean transcript with the rendered answers.
function printChat() {
  window.print();
}

// Example questions shown on the empty landing state. Clicking one prefills the
// composer (which lives in the page template, outside this component) via an
// event the glue listens for, so the user can review and send.
const examplePrompts = [
  'What does this design do?',
  'List the power rails and their sources.',
  'What are the main ICs and their functions?',
];
function prefill(text: string) {
  document.dispatchEvent(new CustomEvent('drcy-chat-prefill', {detail: text}));
}

async function loadHistory() {
  try {
    const res = await GET(`${baseUrl}/history?${scopeQuery}`);
    if (!res.ok) return;
    const data = await res.json();
    for (const entry of data.messages ?? []) upsert(entry);
  } catch (err) {
    console.error('Failed to load DRCY chat history', err);
  }
}

function hasInFlightTurn(): boolean {
  return messages.value.some(
    (m) => m.role === 'assistant' && (m.status === 'pending' || m.status === 'running'),
  );
}

// Start polling when a turn is in flight, stop when none is; also tell the
// composer (which lives in the page template, outside this component) to disable
// send while a turn responds and re-enable it once done. Idempotent, so it's
// safe to call after any state change (SSE event, upsert, or a poll tick).
function syncPolling() {
  const busy = hasInFlightTurn();
  if (busy) {
    if (pollTimer === null) {
      pollTimer = window.setInterval(loadHistory, POLL_INTERVAL_MS);
    }
  } else if (pollTimer !== null) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
  if (busy !== lastBusy) {
    lastBusy = busy;
    document.dispatchEvent(new CustomEvent('drcy-chat-busy', {detail: busy}));
  }
}

function openStream() {
  eventSource = new EventSource(`${baseUrl}/stream?${scopeQuery}`);
  eventSource.addEventListener('open', () => {
    // Reconcile history on every open, including the first. Because the stream is
    // subscribed before this catch-up read, no message posted during the read is
    // missed (it is delivered live), and reconnects (server restart, network
    // blip) also catch up instead of hanging on a stale turn.
    loadHistory();
  });
  eventSource.addEventListener('message', (event: MessageEvent) => {
    upsert(JSON.parse(event.data) as MessageEntry);
  });
  eventSource.addEventListener('chunk', (event: MessageEvent) => {
    const {turn_id: turnId, content} = JSON.parse(event.data);
    const assistant = assistantForTurn(turnId);
    if (assistant) {
      assistant.content += content;
      if (assistant.status === 'pending') assistant.status = 'running';
      scheduleRender(assistant);
      scrollToBottom();
    }
  });
  eventSource.addEventListener('activity', (event: MessageEvent) => {
    const {turn_id: turnId, label} = JSON.parse(event.data);
    const assistant = assistantForTurn(turnId);
    if (assistant && label) {
      // Show only the tool running right now; keep a running count for the
      // end-of-turn summary.
      assistant.activity = label;
      assistant.toolCount += 1;
      scrollToBottom();
    }
  });
  eventSource.addEventListener('status', (event: MessageEvent) => {
    const {turn_id: turnId, status, content} = JSON.parse(event.data);
    const assistant = assistantForTurn(turnId);
    if (assistant) {
      assistant.status = status;
      if (content) assistant.content = content;
      if (status === 'completed' || status === 'failed') {
        assistant.activity = ''; // stop the live tool line; the count remains
        renderNow(assistant);
      } else {
        scheduleRender(assistant);
      }
      scrollToBottom();
      syncPolling();
    }
  });
  eventSource.addEventListener('cleared', () => clearAll());
  eventSource.addEventListener('artifact', (event: MessageEvent) => {
    // Reveal the sidebar only when a new artifact is created; an update, commit,
    // or delete refreshes the list without forcing the panel open each time.
    let action = '';
    try {
      action = JSON.parse(event.data).action;
    } catch {
      // Non-JSON payload: refresh without revealing.
    }
    loadArtifacts(action === 'created');
  });
  eventSource.addEventListener('error', () => {
    // EventSource auto-reconnects on a transient error (and its 'open' handler
    // re-runs the history catch-up). Until it recovers — or if it can't — fall
    // back to the history poll so an in-flight turn still reconciles and reaches
    // a terminal state instead of spinning. syncPolling is a no-op when nothing
    // is in flight, so this costs nothing on an idle stream.
    syncPolling();
  });
}

// The composer glue (features/drcy-chat.ts) lives outside this component and
// coordinates through the server, but on a successful send/end it also signals us
// directly so the view updates even when SSE is dead or on another Hub process:
// 'reconcile' reloads history (showing the new turn and starting the fallback
// poll), 'ended' clears the transcript locally.
function onReconcile() {
  loadHistory();
}
function onEnded() {
  clearAll();
}

onMounted(() => {
  // Open the stream first; its 'open' handler does the initial history load, so
  // the subscription is active before the catch-up read (no missed-message gap).
  openStream();
  loadArtifacts(true);
  window.addEventListener('beforeunload', warnBeforeUnload);
  window.addEventListener('keydown', handleArtifactKeydown);
  document.addEventListener('drcy-chat-reconcile', onReconcile);
  document.addEventListener('drcy-chat-ended', onEnded);
});

onUnmounted(() => {
  if (eventSource) eventSource.close();
  if (pollTimer !== null) clearInterval(pollTimer);
  for (const timer of renderTimers.values()) clearTimeout(timer);
  renderTimers.clear();
  window.removeEventListener('beforeunload', warnBeforeUnload);
  window.removeEventListener('keydown', handleArtifactKeydown);
  document.removeEventListener('drcy-chat-reconcile', onReconcile);
  document.removeEventListener('drcy-chat-ended', onEnded);
});
</script>

<template>
  <div class="drcy-chat">
    <div v-if="messages.length || artifacts.length" class="drcy-chat-toolbar tw:flex tw:justify-end tw:gap-2 tw:mb-2">
      <button
        v-if="artifacts.length"
        class="ui tiny basic button"
        type="button"
        :aria-expanded="artifactSidebarOpen"
        aria-controls="drcy-chat-artifact-sidebar"
        @click="artifactSidebarOpen = true"
      >
        Artifacts <span class="ui mini label">{{ artifacts.length }}</span>
      </button>
      <button v-if="messages.length" class="ui tiny basic button" type="button" @click="exportMarkdown">
        Export Markdown
      </button>
      <button v-if="messages.length" class="ui tiny basic button" type="button" @click="printChat">
        Save as PDF
      </button>
    </div>
    <div ref="scroller" class="drcy-chat-messages">
    <div v-if="messages.length === 0" class="drcy-chat-empty">
      <img :src="drcyAvatar" class="drcy-chat-empty-avatar" alt="">
      <div class="drcy-chat-empty-title">Chat with DRCY about this design</div>
      <div class="drcy-chat-empty-subtitle">
        Ask about components, nets, connections, or part specs. Every answer is backed by evidence you can check.
      </div>
      <div class="drcy-chat-empty-examples">
        <button
          v-for="(example, i) in examplePrompts"
          :key="i"
          type="button"
          class="ui basic button drcy-chat-example"
          @click="prefill(example)"
        >{{ example }}</button>
      </div>
    </div>
    <div v-else class="comment-list">
     <div class="timeline">
      <div
        v-for="message in messages"
        :key="message.id"
        class="timeline-item comment"
        :class="{'drcy-chat-failed': message.status === 'failed'}"
      >
        <a class="timeline-avatar" :href="avatarHref(message)">
          <img class="ui avatar" :src="avatarSrc(message)" width="40" height="40" alt="">
        </a>
        <div class="content comment-container">
          <div class="comment-header" role="heading" aria-level="3">
            <div class="comment-header-left tw:flex tw:items-center">
              <span class="text grey muted-links">
                <strong class="tw:text-text-dark">{{ authorName(message) }}</strong>
              </span>
            </div>
          </div>
          <div class="ui attached segment comment-body" role="article">
            <!-- Streamed/final answer. Kept in the DOM (v-show) so its render
                 target is stable while chunks arrive. -->
            <div v-show="message.content" :ref="(el) => setBubble(message.id, el as Element | null)"></div>
            <!-- Busy state: the animation until text starts, and a single live
                 line for the tool DRCY is running right now. -->
            <template
              v-if="message.role === 'assistant' && (message.status === 'pending' || message.status === 'running')"
            >
              <img v-if="!message.content" :src="thinkingSrc" class="drcy-chat-thinking-anim" alt="DRCY is thinking…">
              <div v-if="message.activity" class="drcy-chat-tool" :class="{'tw:mt-2': message.content}">
                Tool: {{ message.activity }}
              </div>
            </template>
            <!-- Once the turn is done, summarize how many tools ran. -->
            <div
              v-if="message.role === 'assistant' && (message.status === 'completed' || message.status === 'failed') && message.toolCount"
              class="drcy-chat-toolcount tw:mt-2"
            >
              {{ message.toolCount }} tool call{{ message.toolCount === 1 ? '' : 's' }}
            </div>
          </div>
        </div>
      </div>
     </div>
    </div>
    </div>
    <Teleport to="#drcy-chat-artifact-sidebar">
      <Transition name="drcy-artifact-drawer">
        <aside
          v-if="(artifacts.length || artifactNotice) && artifactSidebarOpen"
          class="ui segment drcy-artifact-sidebar"
          aria-label="DRCY artifacts"
        >
        <header class="drcy-artifact-sidebar-header">
          <div class="tw:min-w-0">
            <div class="tw:flex tw:items-center tw:gap-2">
              <h3 class="tw:m-0">DRCY Artifacts</h3>
              <span class="ui mini label">{{ artifacts.length }}</span>
            </div>
          </div>
          <button
            class="ui tiny basic button drcy-artifact-close"
            type="button"
            aria-label="Collapse artifact sidebar"
            @click="closeArtifactSidebar"
          >
            Hide
          </button>
        </header>

        <div v-if="artifactError" class="ui negative message tw:mx-3 tw:my-2 tw:py-2">{{ artifactError }}</div>
        <div v-else-if="artifactNotice" class="ui positive message tw:mx-3 tw:my-2 tw:py-2">
          {{ artifactNotice }}
          <a v-if="artifactNoticeUrl" :href="artifactNoticeUrl">View commit</a>
        </div>

        <div class="drcy-artifact-list" role="list" aria-label="Artifact drafts">
          <article
            v-for="artifact in artifacts"
            :key="artifact.id"
            class="drcy-artifact-list-item"
            :class="{'active': activeArtifactId === artifact.id}"
            role="listitem"
          >
            <button
              type="button"
              class="drcy-artifact-list-button"
              :aria-expanded="activeArtifactId === artifact.id"
              @click="toggleArtifactDetails(artifact)"
            >
              <span class="drcy-artifact-list-copy">
                <span class="drcy-artifact-list-title">{{ artifact.title }}</span>
                <span class="tw:text-text-light-2 tw:text-xs">{{ artifact.filename }}</span>
              </span>
              <span class="ui mini label">{{ artifact.format.toUpperCase() }}</span>
              <span class="drcy-artifact-toggle" aria-hidden="true">
                {{ activeArtifactId === artifact.id ? '−' : '+' }}
              </span>
            </button>
            <div v-if="activeArtifactId === artifact.id" class="drcy-artifact-detail">
              <template v-if="editingArtifactId === artifact.id">
                <div class="ui form drcy-artifact-edit-form">
                  <div class="field">
                    <label :for="`drcy-artifact-title-${artifact.id}`">Title</label>
                    <input :id="`drcy-artifact-title-${artifact.id}`" v-model="editTitle">
                  </div>
                  <div class="field">
                    <label :for="`drcy-artifact-filename-${artifact.id}`">Filename</label>
                    <input :id="`drcy-artifact-filename-${artifact.id}`" v-model="editFilename">
                  </div>
                  <div class="field drcy-artifact-content-field">
                    <label :for="`drcy-artifact-content-${artifact.id}`">Content</label>
                    <textarea :id="`drcy-artifact-content-${artifact.id}`" v-model="editContent" class="drcy-artifact-editor"></textarea>
                  </div>
                  <div class="tw:flex tw:gap-2">
                    <button class="ui small primary button" type="button" @click="saveArtifact(artifact)">Save</button>
                    <button class="ui small basic button" type="button" @click="editingArtifactId = null">Cancel</button>
                  </div>
                </div>
              </template>
              <template v-else>
                <div class="drcy-artifact-actions" aria-label="Artifact actions">
                  <button class="ui tiny basic button" type="button" @click="beginEdit(artifact)">
                    Edit
                  </button>
                  <a class="ui tiny basic button" :href="artifactDownloadUrl(artifact)">
                    Download
                  </a>
                  <button class="ui tiny basic button" type="button" :disabled="committing" @click="commitArtifact(artifact)">
                    {{ committing ? 'Saving…' : 'Save to repo' }}
                  </button>
                  <button class="ui tiny basic red button" type="button" @click="deleteArtifact(artifact)">
                    Delete
                  </button>
                </div>
                <pre class="drcy-artifact-preview">{{ artifact.content }}</pre>
              </template>
            </div>
          </article>
        </div>
        </aside>
      </Transition>
    </Teleport>
  </div>
</template>

<style scoped>
.drcy-chat-messages {
  min-height: 12rem;
  max-height: 60vh;
  overflow-y: auto;
}
.drcy-chat-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-height: 15rem;
  text-align: center;
  padding: 1.5rem 1rem;
}
.drcy-chat-empty-avatar {
  width: 56px;
  height: 56px;
  border-radius: 50%;
}
.drcy-chat-empty-title {
  font-size: 1.15em;
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
}
.drcy-chat-empty-subtitle {
  color: var(--color-text-light-2);
  max-width: 34rem;
}
.drcy-chat-empty-examples {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  justify-content: center;
  margin-top: 0.5rem;
}
.drcy-chat-thinking-anim {
  display: block;
  height: 72px;
  width: auto;
}
.drcy-chat-tool {
  color: var(--color-text-light-2);
  font-size: 0.85em;
  font-family: var(--fonts-monospace);
}
.drcy-chat-toolcount {
  color: var(--color-text-light-3);
  font-size: 0.8em;
}
.drcy-chat-failed .comment-body {
  border-color: var(--color-red);
}
.drcy-artifact-sidebar {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: min(calc(100vh - 7.5rem), 64rem);
  min-height: 38rem;
  margin: 0 !important;
  padding: 0 !important;
  overflow: hidden;
  background: var(--color-body);
  border: 1px solid var(--color-secondary) !important;
  border-radius: var(--border-radius) !important;
}
.drcy-artifact-sidebar-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
  border-bottom: 1px solid var(--color-secondary);
}
.drcy-artifact-close {
  margin: 0 !important;
  padding-right: 0.8rem !important;
  padding-left: 0.8rem !important;
}
.drcy-artifact-list {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 0.5rem;
  min-height: 0;
  overflow-y: auto;
  padding: 0.75rem;
}
.drcy-artifact-list-item {
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
  overflow: hidden;
  background: var(--color-body);
  border: 1px solid var(--color-secondary);
  border-radius: var(--border-radius);
}
.drcy-artifact-list-item.active {
  flex: 1 1 auto;
  min-height: 20rem;
  border-color: var(--color-secondary-dark-1);
}
.drcy-artifact-list-button {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 0.65rem;
  width: 100%;
  min-width: 0;
  padding: 0.65rem 0.75rem;
  color: inherit;
  text-align: left;
  background: var(--color-secondary-alpha-20);
  border: 0;
  cursor: pointer;
}
.drcy-artifact-list-copy {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-width: 0;
}
.drcy-artifact-list-copy > span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.drcy-artifact-list-title {
  font-weight: var(--font-weight-semibold);
}
.drcy-artifact-toggle {
  flex: 0 0 1rem;
  font-size: 1.15rem;
  font-weight: var(--font-weight-semibold);
  text-align: center;
}
.drcy-artifact-detail {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
  padding: 0.75rem;
  border-top: 1px solid var(--color-secondary);
}
.drcy-artifact-actions {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.5rem;
  width: 100%;
  margin: 0;
}
.drcy-artifact-actions > .button {
  min-width: 0;
  margin: 0 !important;
  padding-right: 0.5rem !important;
  padding-left: 0.5rem !important;
  white-space: nowrap;
}
.drcy-artifact-preview {
  flex: 1 1 auto;
  min-height: 10rem;
  overflow: auto;
  margin: 0.75rem 0 0;
  padding: 0.75rem;
  background: var(--color-code-bg);
  border: 1px solid var(--color-secondary);
  border-radius: var(--border-radius);
  white-space: pre-wrap;
}
.drcy-artifact-editor {
  flex: 1 1 auto;
  min-height: 16rem;
  width: 100%;
  resize: none;
  font-family: var(--fonts-monospace);
}
.drcy-artifact-edit-form,
.drcy-artifact-content-field {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
}
.drcy-artifact-drawer-enter-active,
.drcy-artifact-drawer-leave-active {
  transition: opacity 200ms ease, transform 200ms ease;
}
.drcy-artifact-drawer-enter-from,
.drcy-artifact-drawer-leave-to {
  opacity: 0;
  transform: translateX(1rem);
}
</style>

<!-- Unscoped: print isolation, plus the repo-level branch picker which lives in
     the page template (outside this component), so it can't be scoped. -->
<style> /* eslint-disable-line vue-scoped-css/enforce-style-type */
/* eslint-disable vue-scoped-css/no-unused-selector */

.drcy-chat-workspace {
  display: flex;
  overflow: clip;
  align-items: flex-start;
  margin: 1rem 0;
}
.drcy-chat-main {
  flex: 1 1 auto;
  min-width: 0;
  margin: 0 !important;
}
.drcy-chat-artifact-sidebar-target {
  position: sticky;
  top: 1rem;
  flex: 0 0 0;
  min-width: 0;
  overflow: hidden;
  opacity: 0;
  transition: flex-basis 200ms ease, opacity 160ms ease;
}
.drcy-chat-artifact-sidebar-target:has(> .drcy-artifact-sidebar) {
  flex-basis: clamp(25rem, 38vw, 32rem);
  margin-left: 1rem;
  opacity: 1;
}

@media (max-width: 959.98px) {
  .drcy-chat-artifact-sidebar-target:has(> .drcy-artifact-sidebar) {
    position: fixed;
    z-index: 1001;
    inset: 0 0 0 auto;
    width: min(32rem, 100vw);
    margin-left: 0;
    box-shadow: -8px 0 24px var(--color-shadow);
  }
  .drcy-artifact-sidebar {
    position: static;
    width: 100%;
    height: 100%;
    min-height: 0;
    border-radius: 0 !important;
  }
}

@media (max-width: 479.98px) {
  .drcy-artifact-actions {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (prefers-reduced-motion: reduce) {
  .drcy-chat-artifact-sidebar-target,
  .drcy-artifact-drawer-enter-active,
  .drcy-artifact-drawer-leave-active {
    transition: none;
  }
}

@media print {
  body * {
    visibility: hidden;
  }
  .drcy-chat-panel,
  .drcy-chat-panel * {
    visibility: visible;
  }
  .drcy-chat-panel {
    position: absolute;
    inset: 0;
    margin: 0;
    border: none;
    box-shadow: none;
  }
  .drcy-chat-toolbar,
  .drcy-chat-end,
  .drcy-chat-compose-area,
  .drcy-chat-artifact-sidebar-target {
    display: none !important;
  }
  .drcy-chat-messages {
    max-height: none !important;
    overflow: visible !important;
  }
}
</style>
